import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { message } from "antd";
import { isDebugPanelExpandedByDefault } from "../utils/debugFloatEnabled";
import {
  getApiLastErrorSnapshot,
  subscribeApiLastError,
} from "../utils/apiLastError";
import {
  clearHisDebugLog,
  getHisDebugLogSnapshot,
  subscribeHisDebugLog,
} from "../utils/hisDebugLog";
import {
  getLayoutDebugSnapshot,
  subscribeLayoutDebug,
  type LayoutBoxSlice,
} from "../utils/layoutDebugSnapshot";
import {
  decodeJwtPayloadUnsafe,
  formatJwtExpHint,
} from "../utils/hisJwtDebug";
import { getMsOrgApiBaseUrl } from "../service/wecomAuthService";

const PANEL_Z = 2147483647;
const POS_STORAGE_KEY = "his_debug_float_pos";

function readToken(): string | null {
  return (
    localStorage.getItem("token")?.trim() ||
    sessionStorage.getItem("token")?.trim() ||
    null
  );
}

function loadSavedPos(): { left: number; top: number } | null {
  try {
    const raw = localStorage.getItem(POS_STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as { left?: unknown; top?: unknown };
    if (typeof p.left === "number" && typeof p.top === "number") {
      return { left: p.left, top: p.top };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function defaultPos(): { left: number; top: number } {
  if (typeof window === "undefined") return { left: 16, top: 16 };
  return {
    left: Math.max(8, window.innerWidth - 78),
    top: Math.max(8, window.innerHeight - 132),
  };
}

function clampPos(
  left: number,
  top: number,
  elW: number,
  elH: number
): { left: number; top: number } {
  if (typeof window === "undefined") return { left, top };
  const m = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    left: Math.min(Math.max(m, left), Math.max(m, vw - elW - m)),
    top: Math.min(Math.max(m, top), Math.max(m, vh - elH - m)),
  };
}

function pickSearchParams(queryString: string): Record<string, string> {
  const out: Record<string, string> = {};
  const p = new URLSearchParams(queryString);
  p.forEach((value, key) => {
    if (value) out[key] = value;
  });
  return out;
}

function formatLayoutSlice(s: LayoutBoxSlice | undefined): string {
  if (!s) return "—";
  return `cH=${s.clientHeight} sH=${s.scrollHeight} oH=${s.offsetHeight} sTop=${Math.round(s.scrollTop)}`;
}

function heapMb(n: number | undefined): string | undefined {
  if (n == null || !Number.isFinite(n)) return undefined;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}

function mergeUrlParams(search: string, hash: string): Record<string, string> {
  const fromSearch = pickSearchParams(
    search.startsWith("?") ? search.slice(1) : search
  );
  const qIdx = hash.indexOf("?");
  const fromHash =
    qIdx >= 0 ? pickSearchParams(hash.slice(qIdx + 1)) : {};
  return { ...fromHash, ...fromSearch };
}

/**
 * 企微内排查：环境、路由、可见性、token 摘要、最近 API 失败、环形事件日志（含门店授权重拉原因）。
 */
export default function DebugFloatPanel() {
  const location = useLocation();
  const lastApiError = useSyncExternalStore(
    subscribeApiLastError,
    getApiLastErrorSnapshot,
    () => null
  );
  const debugLines = useSyncExternalStore(
    subscribeHisDebugLog,
    getHisDebugLogSnapshot,
    () => []
  );
  const layoutSnap = useSyncExternalStore(
    subscribeLayoutDebug,
    getLayoutDebugSnapshot,
    (): ReturnType<typeof getLayoutDebugSnapshot> => ({ kind: "empty" })
  );

  const [expanded, setExpanded] = useState(isDebugPanelExpandedByDefault);
  const [pos, setPos] = useState<{ left: number; top: number }>(
    () => loadSavedPos() ?? defaultPos()
  );
  const [token, setToken] = useState<string | null>(() => readToken());
  const panelRef = useRef<HTMLDivElement>(null);
  const collapsedRef = useRef<HTMLButtonElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originLeft: number;
    originTop: number;
    moved: boolean;
  } | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => {
      try {
        localStorage.setItem(POS_STORAGE_KEY, JSON.stringify(pos));
      } catch {
        /* ignore */
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [pos]);

  useLayoutEffect(() => {
    const clamp = () => {
      setPos((p) => {
        const el = expanded ? panelRef.current : collapsedRef.current;
        const w = el?.offsetWidth ?? (expanded ? 320 : 52);
        const h = el?.offsetHeight ?? (expanded ? 240 : 40);
        return clampPos(p.left, p.top, w, h);
      });
    };
    clamp();
    window.addEventListener("resize", clamp);
    return () => window.removeEventListener("resize", clamp);
  }, [expanded]);

  useEffect(() => {
    const id = window.setInterval(() => setToken(readToken()), 2000);
    return () => window.clearInterval(id);
  }, []);

  const handleDragMove = useCallback(
    (e: React.PointerEvent) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (Math.abs(dx) + Math.abs(dy) > 4) d.moved = true;
      const el = expanded ? panelRef.current : collapsedRef.current;
      const w = el?.offsetWidth ?? (expanded ? 320 : 52);
      const h = el?.offsetHeight ?? (expanded ? 240 : 40);
      setPos(clampPos(d.originLeft + dx, d.originTop + dy, w, h));
    },
    [expanded]
  );

  const handleDragEnd = useCallback(
    (e: React.PointerEvent, onTap?: () => void) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      try {
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const wasDrag = d.moved;
      dragRef.current = null;
      if (!wasDrag && onTap) onTap();
    },
    []
  );

  const bindDrag = useCallback(
    (onTap?: () => void) => ({
      onPointerDown: (e: React.PointerEvent) => {
        if (e.button !== 0) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        dragRef.current = {
          pointerId: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          originLeft: pos.left,
          originTop: pos.top,
          moved: false,
        };
      },
      onPointerMove: handleDragMove,
      onPointerUp: (e: React.PointerEvent) => handleDragEnd(e, onTap),
      onPointerCancel: (e: React.PointerEvent) => handleDragEnd(e, onTap),
    }),
    [handleDragEnd, handleDragMove, pos.left, pos.top]
  );

  const urlParams = useMemo(
    () => mergeUrlParams(location.search || "", location.hash || ""),
    [location.search, location.hash]
  );

  const jwtPayload = useMemo(() => decodeJwtPayloadUnsafe(token), [token]);

  const snapshot = useMemo(() => {
    const origin = globalThis.location?.origin ?? "";
    const layoutMetrics =
      layoutSnap.kind === "ready"
        ? {
            ts: layoutSnap.data.ts,
            path: layoutSnap.data.path,
            windowInner: layoutSnap.data.windowInner,
            documentElement: layoutSnap.data.documentElement,
            memory:
              layoutSnap.data.memory &&
              (layoutSnap.data.memory.usedJSHeapSize != null ||
                layoutSnap.data.memory.totalJSHeapSize != null)
                ? {
                    used: heapMb(layoutSnap.data.memory.usedJSHeapSize),
                    total: heapMb(layoutSnap.data.memory.totalJSHeapSize),
                    limit: heapMb(layoutSnap.data.memory.jsHeapSizeLimit),
                  }
                : null,
            leftScroll: layoutSnap.data.leftScroll ?? null,
            storeContent: layoutSnap.data.storeContent ?? null,
            revenueMonitor: layoutSnap.data.revenueMonitor ?? null,
          }
        : null;
    return {
      path: location.pathname + location.search + (location.hash || ""),
      href: globalThis.location?.href ?? "",
      origin,
      visibilityState:
        typeof document !== "undefined" ? document.visibilityState : "",
      hidden: typeof document !== "undefined" ? document.hidden : null,
      userAgent:
        typeof navigator !== "undefined"
          ? navigator.userAgent.slice(0, 180)
          : "",
      msOrgApiBase: getMsOrgApiBaseUrl(),
      urlParams,
      tokenPresent: Boolean(token),
      tokenLength: token?.length ?? 0,
      jwtExp: formatJwtExpHint(jwtPayload),
      jwtUserId:
        jwtPayload && jwtPayload.userId != null
          ? String(jwtPayload.userId)
          : null,
      lastApiFailure: lastApiError,
      layoutMetrics,
      debugTail: [...debugLines].slice(-48),
    };
  }, [
    location.pathname,
    location.search,
    location.hash,
    urlParams,
    token,
    jwtPayload,
    lastApiError,
    debugLines,
    layoutSnap,
  ]);

  const copyAll = useCallback(async () => {
    try {
      const text = JSON.stringify(snapshot, null, 2);
      await navigator.clipboard.writeText(text);
      void message.success("已复制调试信息");
    } catch {
      void message.error("复制失败");
    }
  }, [snapshot]);

  const btnStyle: React.CSSProperties = {
    fontSize: 11,
    padding: "4px 10px",
    borderRadius: 6,
    border: "1px solid rgba(52,211,153,0.4)",
    background: "rgba(6,78,59,0.5)",
    color: "#a7f3d0",
    cursor: "pointer",
    touchAction: "manipulation",
  };

  const row = (label: string, value: string | null | undefined) => (
    <div
      key={label}
      style={{
        marginBottom: 6,
        wordBreak: "break-all" as const,
        fontSize: 11,
        lineHeight: 1.45,
      }}
    >
      <span style={{ color: "#6ee7b7", fontWeight: 600 }}>{label}</span>
      <span style={{ color: "#d1fae5" }}>
        {value === undefined || value === null || value === ""
          ? "—"
          : String(value)}
      </span>
    </div>
  );

  const panel = expanded ? (
    <div
      ref={panelRef}
      style={{
        position: "fixed",
        left: pos.left,
        top: pos.top,
        zIndex: PANEL_Z,
        width: "min(92vw, 360px)",
        maxHeight: "52vh",
        overflow: "auto",
        padding: "10px 12px",
        borderRadius: 12,
        border: "2px solid rgba(0,255,136,0.45)",
        background: "rgba(10,14,12,0.96)",
        boxShadow: "0 8px 28px rgba(0,0,0,0.5)",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <div
        {...bindDrag()}
        title="按住拖动"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
          gap: 8,
          minHeight: 36,
          cursor: "grab",
          touchAction: "none",
          userSelect: "none",
          WebkitUserSelect: "none",
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: "#34d399" }}>
          门店运营 · 调试
        </span>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flexShrink: 0 }}>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => {
              clearHisDebugLog();
              void message.info("已清空事件日志");
            }}
            style={btnStyle}
          >
            清日志
          </button>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={copyAll}
            style={btnStyle}
          >
            复制
          </button>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setExpanded(false)}
            style={{ ...btnStyle, borderColor: "rgba(148,163,184,0.35)", color: "#94a3b8" }}
          >
            收起
          </button>
        </div>
      </div>

      <div
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      >
        {lastApiError ? (
          <div
            style={{
              marginBottom: 10,
              paddingBottom: 8,
              borderBottom: "1px solid rgba(239,68,68,0.35)",
            }}
          >
            <div style={{ color: "#fca5a5", fontWeight: 700, marginBottom: 6 }}>
              最近 API 失败
            </div>
            {row("分类", lastApiError.categoryLabel)}
            {row("URL", lastApiError.requestUrl)}
            {row("状态", lastApiError.status != null ? String(lastApiError.status) : undefined)}
            {row("说明", lastApiError.hint)}
          </div>
        ) : null}

        {row("visibility", snapshot.visibilityState)}
        {row("document.hidden", snapshot.hidden != null ? String(snapshot.hidden) : "—")}
        {row("path", snapshot.path)}
        {row("msOrg base", snapshot.msOrgApiBase)}
        {row("token", snapshot.tokenPresent ? `有（${snapshot.tokenLength} 字符）` : "无")}
        {row("JWT exp", snapshot.jwtExp)}
        {row("JWT userId", snapshot.jwtUserId)}
        {row("UA(截断)", snapshot.userAgent)}

        <div style={{ marginTop: 10, marginBottom: 4, color: "#6ee7b7", fontWeight: 600 }}>
          布局采样（约 1.2s）
        </div>
        {snapshot.layoutMetrics ? (
          <>
            {row("viewport", `inner ${snapshot.layoutMetrics.windowInner.width}×${snapshot.layoutMetrics.windowInner.height} dpr=${snapshot.layoutMetrics.windowInner.dpr}`)}
            {row("docEl client", `${snapshot.layoutMetrics.documentElement.clientWidth}×${snapshot.layoutMetrics.documentElement.clientHeight}`)}
            {snapshot.layoutMetrics.memory ? (
              <>
                {row("heap used", snapshot.layoutMetrics.memory.used)}
                {row("heap total", snapshot.layoutMetrics.memory.total)}
              </>
            ) : (
              row("heap", "—（仅部分 Chromium）")
            )}
            {snapshot.layoutMetrics.revenueMonitor ? (
              <>
                <div style={{ marginTop: 6, marginBottom: 2, color: "#86efac", fontSize: 10 }}>
                  门店营收监控（容器 / 页壳 / 左栏分区 / 列表 / AI）
                </div>
                {row(
                  "#root",
                  formatLayoutSlice(snapshot.layoutMetrics.revenueMonitor.root),
                )}
                {row(
                  "body",
                  formatLayoutSlice(snapshot.layoutMetrics.revenueMonitor.body),
                )}
                {row(
                  "页壳(#root首子)",
                  formatLayoutSlice(snapshot.layoutMetrics.revenueMonitor.appShell),
                )}
                {row(
                  "监控容器",
                  formatLayoutSlice(
                    snapshot.layoutMetrics.revenueMonitor.monitorContainer,
                  ),
                )}
                {row(
                  "Splitter",
                  formatLayoutSlice(snapshot.layoutMetrics.revenueMonitor.splitter),
                )}
                {row(
                  "左栏 panel",
                  formatLayoutSlice(snapshot.layoutMetrics.revenueMonitor.leftPanel),
                )}
                {row(
                  "左内层 grid",
                  formatLayoutSlice(snapshot.layoutMetrics.revenueMonitor.leftInner),
                )}
                {row(
                  "统计区",
                  formatLayoutSlice(snapshot.layoutMetrics.revenueMonitor.statCards),
                )}
                {row(
                  "搜索区",
                  formatLayoutSlice(snapshot.layoutMetrics.revenueMonitor.searchWrap),
                )}
                {row(
                  "列表 host",
                  formatLayoutSlice(snapshot.layoutMetrics.revenueMonitor.listHost),
                )}
                {row(
                  "列表滚动",
                  formatLayoutSlice(snapshot.layoutMetrics.revenueMonitor.listScroll),
                )}
                {row(
                  "右栏 panel",
                  formatLayoutSlice(snapshot.layoutMetrics.revenueMonitor.rightPanel),
                )}
                {row(
                  "AI 包裹层",
                  formatLayoutSlice(snapshot.layoutMetrics.revenueMonitor.aiWrap),
                )}
              </>
            ) : (
              <>
                {row(
                  "leftScroll(单店)",
                  formatLayoutSlice(
                    snapshot.layoutMetrics.leftScroll ?? undefined,
                  ),
                )}
                {row(
                  "storeContent(单店)",
                  formatLayoutSlice(
                    snapshot.layoutMetrics.storeContent ?? undefined,
                  ),
                )}
              </>
            )}
          </>
        ) : (
          row("布局", "未采样（非监控/单店或 DBG 关闭）")
        )}

        <div style={{ marginTop: 10, marginBottom: 4, color: "#6ee7b7", fontWeight: 600 }}>
          事件日志（含门店列表重拉、layout:*）
        </div>
        <pre
          style={{
            margin: 0,
            maxHeight: 160,
            overflow: "auto",
            fontSize: 10,
            lineHeight: 1.4,
            color: "#a7f3d0",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {debugLines.length === 0 ? "（暂无）" : snapshot.debugTail.join("\n")}
        </pre>
      </div>
    </div>
  ) : (
    <button
      ref={collapsedRef}
      type="button"
      title="按住拖动；轻点展开"
      {...bindDrag(() => setExpanded(true))}
      style={{
        position: "fixed",
        left: pos.left,
        top: pos.top,
        zIndex: PANEL_Z,
        minWidth: 48,
        minHeight: 36,
        padding: "8px 12px",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.5,
        border: "2px solid rgba(0,255,136,0.65)",
        borderRadius: 10,
        background: "rgba(10,14,12,0.96)",
        color: "#00ff88",
        boxShadow: "0 4px 20px rgba(0,0,0,0.45)",
        WebkitTapHighlightColor: "transparent",
        cursor: "grab",
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
      }}
    >
      DBG
    </button>
  );

  if (typeof document === "undefined") return null;
  return createPortal(panel, document.body);
}
